import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import LeftSidebar from "./LeftSidebar";
import TopNav from "./TopNav";
import MainContent from "./MainContent";
import { styled, alpha } from "@mui/material/styles";
import { Card, CardContent, CardMedia } from "@mui/material";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import { getLocalStorage } from "./localstorage";
import RightSidebar from "./RightSidebar";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const CourseCard = styled(Card)(({ theme }) => ({
  width: 320,
  margin: theme.spacing(1),
  transition: "transform 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[4],
  },
}));

const ToggleButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ theme, active }) => ({
  backgroundColor: active
    ? theme.palette.primary.main
    : theme.palette.background.paper,
  color: active
    ? theme.palette.primary.contrastText
    : theme.palette.primary.main,
  "&:hover": {
    backgroundColor: active
      ? theme.palette.primary.dark
      : alpha(theme.palette.primary.main, 0.08),
  },
}));

const CourseSection = ({
  courses,
  isAssigned,
  onAssignedToggle,
  onCompletedToggle,
  onCourseClick,
  onCourseDetailClick,
}) => {
  return (
    <Box sx={{ mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
          Active Courses
        </Typography>
        <Box
          sx={{
            display: "flex",
            backgroundColor: "background.paper",
            borderRadius: 1,
            p: 0.5,
          }}
        >
          <ToggleButton
            active={isAssigned}
            onClick={onAssignedToggle}
            sx={{ borderRadius: "4px 0 0 4px" }}
          >
            Assigned
          </ToggleButton>
          <ToggleButton
            active={!isAssigned}
            onClick={onCompletedToggle}
            sx={{ borderRadius: "0 4px 4px 0" }}
          >
            Completed
          </ToggleButton>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "nowrap",
          overflowX: "auto",
          pb: 2,
          "::-webkit-scrollbar": {
            height: 8,
          },
          "::-webkit-scrollbar-track": {
            bgcolor: "background.paper",
          },
          "::-webkit-scrollbar-thumb": {
            bgcolor: "primary.main",
            borderRadius: 4,
          },
        }}
      >
        {(isAssigned ? courses : courses.filter((e) => e.isFinal)).map(
          (course, index) => {
            const percentCompleted = Math.min(
              (course.currentProgress.length / course.total) * 100 || 0,
              100
            );

            return (
              <CourseCard key={index} onClick={() => onCourseClick(course)}>
                <CardMedia
                  component="img"
                  height="180"
                  image={course.thumbnail}
                  alt={course.title}
                  sx={{ objectFit: "cover" }}
                />
                <CardContent>
                  <Typography
                    variant="subtitle1"
                    component="div"
                    sx={{
                      fontWeight: 500,
                      mb: 2,
                      height: 48,
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {course.description}
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={percentCompleted}
                        sx={{ flexGrow: 1, mr: 2, height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {Math.round(percentCompleted)}%
                      </Typography>
                    </Box>
                  </Box>
                  {course.isCompleted && (
                    <Button
                      variant="outlined"
                      color="primary"
                      fullWidth
                      onClick={(e) => {
                        e.stopPropagation();
                        onCourseDetailClick(course);
                      }}
                      sx={{ textTransform: "none" }}
                    >
                      Resume Course
                    </Button>
                  )}
                </CardContent>
              </CourseCard>
            );
          }
        )}
      </Box>
    </Box>
  );
};

const UserHomeScreen = ({ onCourseClick, onCourseDetailClick, onBack }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [awarnessCourses, setAwarnessCourses] = useState([]);
  const [isAwarenessAssigned, setIsAwarenessAssigned] = useState(true);
  const [currentScreen, setCurrentScreen] = useState(10);
  const [firstName, setFirstName] = useState("");
  const [companyLogo, setCompanyLogo] = useState("");
  const [awarnessCoursesAll, setAwarnessCoursesAll] = useState([]);

  const fetchProgressData = async (userId, token) => {
    // Fetch progress data here
    const response = await fetch(
      `${API_URL}/api/progress/${userId}`,
      {
        headers: {
          Authorization: token,
        },
      }
    );
    if (!response.ok) {
      if (response.status == 401) {
        localStorage.clear();
        window.location.reload();
        return [];
      }
    }
    return response.json();
  };

  const fetchCoursesData = async (token, company) => {
    // Fetch courses data here
    const response = await fetch(
      `${API_URL}/admin/courses-company-main/${company}`,
      {
        headers: {
          Authorization: token,
        },
      }
    );
    if (!response.ok) {
      if (response.status == 401) {
        localStorage.clear();
        window.location.reload();
        return [];
      }
    }
    return response.json();
  };

  useEffect(() => {
    const getCourses = async () => {
      try {
        const prefs = getLocalStorage();
        const userId = prefs.userId;
        const token = prefs.token;
        setFirstName(prefs.firstName || "");
        setCompanyLogo(prefs.companyLogo || "");

        const parsedData = await fetchProgressData(userId, token);
        const coursesJson = await fetchCoursesData(token, prefs.company);
        const awarnessCoursesAll = [];
        for (const courseJson of coursesJson) {
          const element = parsedData.find(
            (_element) => _element.course === courseJson._id
          );
          const course = {
            id: courseJson._id,
            title: courseJson.title,
            description: courseJson.description,
            thumbnail: `${API_URL}/${courseJson.thumbnail.replace(
              " ",
              "%20"
            )}`,
            isPopular: courseJson.isPopular,
            continuousCourse: courseJson.continuousCourse,
            showConsentForm: courseJson.showConsentForm,
            isFinal: element ? element.isFinal : false,
            isCompleted: element ? element.isCompleted : false,
            total: element ? element.total : 0,
            currentProgress: element ? element.currentProgress : [],
            progressID: element ? element._id : "",
            consentText: courseJson.consentText,
            modules: courseJson.modules,
          };
          awarnessCoursesAll.push(course);
        }
        awarnessCoursesAll.sort((a, b) =>
          a.isFinal.toString().localeCompare(b.isFinal.toString())
        );
        setAwarnessCoursesAll(awarnessCoursesAll);
        setAwarnessCourses(awarnessCoursesAll);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching courses:", error);
        setIsLoading(false);
        // Handle error
      }
    };
    getCourses();
  }, []);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          minHeight: "100vh",
          bgcolor: "background.default",
        }}
      >
        <Box
          sx={{
            width: "15%",
            bgcolor: "#fff",
            boxShadow: 3,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <LeftSidebar
            currentScreen={currentScreen}
            setCurrentScreen={setCurrentScreen}
          />
        </Box>

        {/* Main Content Area - 70% width */}
        <Box
          sx={{
            width: "63%", // Adjusted width
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              overflow: "auto",
              background: "#e7f2fb",
            }}
          >
            <MainContent
              currentScreen={currentScreen}
              isLoading={isLoading}
              awarnessCourses={awarnessCourses}
              isAwarenessAssigned={isAwarenessAssigned}
              setIsAwarenessAssigned={setIsAwarenessAssigned}
              onCourseClick={onCourseClick}
              onCourseDetailClick={onCourseDetailClick}
            />
          </Box>
        </Box>

        {/* Right Sidebar - 13% width */}
        <Box
          sx={{
            width: "16%",
            bgcolor: "#063685eb",
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            padding: 2,
            boxShadow: 1,
          }}
        >
          <RightSidebar />
        </Box>
      </Box>
    </>
  );
};

export default UserHomeScreen;
